import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRating,
  ClimateRiskRatingsByIndex,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '../widgets/WidgetSlotRenderer';

const targetYear = new Date().getFullYear() + 20;

const useRiskDescription = (
  riskFactType: CLIMATE_RISK_FACT_TYPES,
  currentRiskLevel: string,
  projectedRiskLevel: string,
) => {
  const { t } = useTranslation();
  const riskName = t(`climateRisk.riskTypes.${riskFactType}.title`);
  if (currentRiskLevel === ClimateRiskRating.not_relevant) {
    return t('climateRisk.riskDescriptions.noCurrentRisk', { riskName });
  }

  if (projectedRiskLevel === currentRiskLevel) {
    return t('climateRisk.riskDescriptions.sameRiskLevel', {
      riskName,
      // TODO: Lokalise
      currentLevel: currentRiskLevel.replace('_', ' '),
    });
  }

  const currentLevelText = currentRiskLevel.replace('_', ' ');
  const projectedLevelText = projectedRiskLevel.replace('_', ' ');

  if (projectedRiskLevel === ClimateRiskRating.not_relevant) {
    return t('climateRisk.riskDescriptions.noRiskProjected', { riskName });
  }

  return t('climateRisk.riskDescriptions.riskChange', {
    riskName,
    currentLevel: currentLevelText,
    projectedLevel: projectedLevelText,
    targetYear,
  });
};

type RiskOverviewProps = {
  risks: CLIMATE_RISK_FACT_TYPES[];
};

export const RiskOverview = (props: RiskOverviewProps) => {
  const { plotId } = useParams<{ plotId: string }>();

  if (plotId) {
    return <PlotRiskOverview {...props} />;
  }

  return <OrgRiskOverview {...props} />;
};

const PlotRiskOverview = ({ risks }: RiskOverviewProps) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [selectedRisk, setSelectedRisk] = useState<CLIMATE_RISK_FACT_TYPES>(risks[0]!);

  const facts = usePlotFacts([selectedRisk, `${selectedRisk}_graph`]);

  const riskFact = getFactFromFactArray(facts, selectedRisk, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });
  const riskGraphFact = getFactFromFactArray(facts, `${selectedRisk}_graph`, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.moderate,
  });

  return (
    <CommonRiskOverview
      risks={risks}
      riskGraphFact={riskGraphFact}
      selectedRisk={riskFact}
      setSelectedRisk={setSelectedRisk}
    />
  );
};

const OrgRiskOverview = ({ risks }: RiskOverviewProps) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [selectedRisk, setSelectedRisk] = useState<CLIMATE_RISK_FACT_TYPES>(risks[0]!);

  const { [selectedRisk]: riskFact } = useOrganizationIndicatorReportingFact([selectedRisk], null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });
  const { [`${selectedRisk}_graph`]: riskGraphFact } = useOrganizationIndicatorGraphFact(
    [`${selectedRisk}_graph`],
    null,
    {
      [PlotReportFactLabelNameEnum.scenario]: Scenario.moderate,
    },
  );

  return (
    <CommonRiskOverview
      risks={risks}
      riskGraphFact={riskGraphFact}
      selectedRisk={riskFact}
      setSelectedRisk={setSelectedRisk}
      isOrganizationFact
    />
  );
};

const CommonRiskOverview = ({
  risks,
  riskGraphFact,
  selectedRisk,
  setSelectedRisk,
  isOrganizationFact,
}: {
  risks: string[];
  selectedRisk: PlotReportFactElement<unknown> | undefined;
  setSelectedRisk: (risk: CLIMATE_RISK_FACT_TYPES) => void;
  riskGraphFact?: PlotReportFactElement<unknown>;
  isOrganizationFact?: boolean;
}) => {
  const { t } = useTranslation();

  const selectedRiskType = selectedRisk?.name as CLIMATE_RISK_FACT_TYPES;

  const currentRiskLevel = isOrganizationFact
    ? ClimateRiskRatingsByIndex[(selectedRisk?.value as number) ?? 0]
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (selectedRisk?.value as Record<string, any>)?.rating || ClimateRiskRating.not_relevant;

  let projectedRiskLevel = currentRiskLevel;

  const graphData =
    (riskGraphFact?.value as Array<{
      date: string;
      value: { rating: string } | number;
    }>) ?? [];

  let minDifference = Infinity;

  graphData.forEach((item) => {
    const year = new Date(item.date).getFullYear();
    const difference = Math.abs(year - targetYear);

    if (difference < minDifference) {
      minDifference = difference;
      // TODO: currently the graph fact data for organization is not in the same format as the plot, so we need to map the values to the correct risk level
      projectedRiskLevel = isOrganizationFact
        ? ClimateRiskRatingsByIndex[item?.value as number]
        : (item.value as { rating: string }).rating;
    }
  });

  const riskDescription = useRiskDescription(selectedRiskType, currentRiskLevel, projectedRiskLevel);
  if (isNullish(selectedRiskType)) return null;

  const config = {
    name: 'climate_risk_overview',
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      header: t('climateRisk.riskOverview'),
      styles: {
        root: 'border-none',
        header: 'border-none typography-h3 text-text-primary px-0',
        body: 'border border-neutral-black-12 rounded-2xl flex-col flex gap-4 p-8',
      },
    },
    widgets: [
      {
        name: 'climate_risk_overview',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex flex-col lg:flex-row gap-8 justify-center',
        },
        widgets: [
          {
            name: 'climate_risks',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'flex-1',
            },
            widgets: [
              {
                name: 'climate_risk_list',
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  data: risks.map((risk) => ({
                    riskFactType: risk,
                  })),
                },
                repeat: {
                  data: 'relatedData.data',
                  key: 'riskFactType',
                  template: {
                    name: 'risk_info',
                    type: WidgetType.o_c_risk_info,
                    visible: true,
                    relatedData: {
                      selectedRiskFactType: selectedRiskType,
                      onSelectRisk: setSelectedRisk,
                    },
                  },
                },
              },
            ],
          },
          {
            name: 'divider',
            type: WidgetType.a_stack,
            visible: risks.length > 0,
            relatedData: {
              className: 'border-b border-neutral-black-12 pb-4 lg:border-r lg:border-b-0',
            },
          },
          {
            name: 'chart_data',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'flex-1 gap-6',
            },
            widgets: [
              {
                name: `risk_preview_${selectedRiskType}`,
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  className: 'gap-1',
                },
                widgets: [
                  {
                    name: `risk_name_${selectedRiskType}`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: (
                        <>
                          <strong>{t(`climateRisk.riskTypes.${selectedRiskType}.title`)}: </strong>
                          {t('climateRisk.riskProjection.projectedLevel')}
                        </>
                      ),
                      className: 'typography-body1 text-text-primary',
                    },
                  },
                  {
                    name: `risk_description_${selectedRiskType}`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: riskDescription,
                      className: 'typography-caption text-text-secondary',
                    },
                  },
                ],
              },
              {
                name: 'climate_risk_chart',
                type: WidgetType.o_c_risk_chart,
                visible: true,
                relatedData: {
                  riskFactType: selectedRiskType,
                },
              },
            ],
          },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={config} />;
};
